// SubscriptionPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { auth, db } from '../../../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { format } from 'date-fns';
import Preloader from '../../Preloader';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ---- Helpers ----
const currencyFmt = (amount = 0, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const fmtEpochSeconds = (secs) => {
  if (!secs || Number.isNaN(secs)) return '';
  try {
    return format(new Date(secs * 1000), 'MMM d, yyyy');
  } catch {
    return '';
  }
};

const isActiveStatus = (status) => status === 'active';
const isAlmostActive = (status) => status === 'trialing' || status === 'past_due';

// -------------------
function SubscriptionPortal({ functionsBase }) {
  const stripe = useStripe();
  const elements = useElements();

  const [user, setUser] = useState(null);
  const [plans, setPlans] = useState([]);
  const [terms, setTerms] = useState([]);

  const [planId, setPlanId] = useState('');
  const [termId, setTermId] = useState('');

  const [allSubs, setAllSubs] = useState([]); // raw subscription docs for user
  const [activeSub, setActiveSub] = useState(null); // one active sub (if any)
  const [activeSubDetails, setActiveSubDetails] = useState(null); // details via CF

  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState(null);

  const [inlineError, setInlineError] = useState(null);
  const [inlineSuccess, setInlineSuccess] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  // ---------- Boot: load user, plans, terms ----------
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setLoading(false);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        // User profile
        const uSnap = await getDoc(doc(db, 'users', uid));
        if (!uSnap.exists()) throw new Error('User not found');
        const u = { uid, ...uSnap.data() };
        setUser(u);

        // Plans (role-scoped)
        const pQ = query(
          collection(db, 'plans'),
          where('role', '==', u.role),
          where('isActive', '==', true)
        );
        const pSn = await getDocs(pQ);
        const pList = pSn.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPlans(pList);
        if (pList.length) setPlanId(pList[0].id);

        // Terms (active only, sorted by startDate)
        const tSn = await getDocs(collection(db, 'institutes', u.institution, 'terms'));
        const tList = tSn.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((t) => t.isActive)
          .sort((a, b) => (a?.startDate?.seconds || 0) - (b?.startDate?.seconds || 0));
        setTerms(tList);
      } catch (e) {
        console.error(e);
        setBootError(e.message || 'Failed to initialize.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ---------- Listen for ALL subscriptions for this user ----------
  useEffect(() => {
    if (!user) return;
    const subsQ = query(collection(db, 'subscriptions'), where('userId', '==', user.uid));
    const unsub = onSnapshot(
      subsQ,
      (snap) => {
        const subs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllSubs(subs);

        // Pick an active subscription if present, else a trialing/past_due (as a fallback)
        const act =
          subs.find((s) => isActiveStatus(s?.status)) ||
          subs.find((s) => isAlmostActive(s?.status)) ||
          null;

        setActiveSub(act || null);
      },
      (err) => {
        console.error(err);
        setBootError('Error loading subscriptions.');
      }
    );
    return () => unsub();
  }, [user]);

  // ---------- Fetch details for active subscription (for richer UI) ----------
  useEffect(() => {
    if (!user || !activeSub?.id) {
      setActiveSubDetails(null);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${functionsBase}/getSubscriptionDetails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.uid, subscriptionId: activeSub.id }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load subscription details');
        setActiveSubDetails(data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [user, activeSub, functionsBase]);

  // ---------- Compute dropdown terms (exclude active term) ----------
  const activeTermId = activeSub?.currentTermId || activeSubDetails?.currentTermId || null;

  const termMap = useMemo(() => {
    const m = new Map();
    terms.forEach((t) => m.set(t.id, t));
    return m;
  }, [terms]);

  const payableTerms = useMemo(() => {
    // Only exclude the term where subscription is ACTIVE.
    // If there's no active subscription, all active terms are payable.
    if (!activeSub || !isActiveStatus(activeSub?.status)) return terms;
    return terms.filter((t) => t.id !== activeTermId);
  }, [terms, activeSub, activeTermId]);

  // Seed term selection when payableTerms updates
  useEffect(() => {
    if (!termId || !payableTerms.find((t) => t.id === termId)) {
      setTermId(payableTerms[0]?.id || '');
    }
  }, [payableTerms, termId]);

  // ---------- Payment handlers ----------
  const handleCardChange = (e) => {
    setCardComplete(e.complete);
    setInlineError(e.error ? e.error.message : null);
  };

  const handleSubscribe = async () => {
    if (!stripe || !elements) return;
    setInlineError(null);
    setInlineSuccess(null);

    if (!planId || !termId) {
      setInlineError('Please select a plan and term.');
      return;
    }
    if (!cardComplete) {
      setInlineError('Please complete your card details.');
      return;
    }

    try {
      setIsProcessing(true);

      // 1) Create SetupIntent
      const setupRes = await fetch(`${functionsBase}/createSetupIntent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid }),
      });
      const setupJson = await setupRes.json();
      if (!setupRes.ok) throw new Error(setupJson.error || 'Could not create SetupIntent.');

      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(
        setupJson.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
              email: user.email,
            },
          },
        }
      );
      if (setupError) throw setupError;

      const paymentMethodId = setupIntent.payment_method;

      // 2) Create subscription
      const subRes = await fetch(`${functionsBase}/createSubscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          planId,
          termId,
          paymentMethodId,
        }),
      });
      const subJson = await subRes.json();
      if (!subRes.ok) throw new Error(subJson.error || 'Subscription failed.');

      // 3) Confirm payment if needed
      if (subJson.clientSecret) {
        const { error: payError } = await stripe.confirmCardPayment(subJson.clientSecret);
        if (payError) throw payError;
      }

      setInlineSuccess('Subscription created!');
    } catch (e) {
      console.error(e);
      setInlineError(e.message || 'Subscription error.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    const subId = activeSub?.id || activeSubDetails?.id;
    if (!subId) return;
    if (!window.confirm('Cancel your active subscription?')) return;

    try {
      setIsProcessing(true);
      const res = await fetch(`${functionsBase}/cancelSubscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, subscriptionId: subId }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || 'Failed to cancel subscription.');
      setInlineSuccess('Subscription canceled.');
    } catch (e) {
      console.error(e);
      setInlineError(e.message || 'Cancel error.');
    } finally {
      setIsProcessing(false);
    }
  };

  // ---------- Derived UI state ----------
  const selectedPlan = plans.find((p) => p.id === planId) || {};
  const selectedTerm = terms.find((t) => t.id === termId) || {};
  const activeTermName = activeTermId ? termMap.get(activeTermId)?.name : null;

  if (loading) return <Preloader />;
  if (!user) return <p className="p-4 text-center">Please sign in to manage subscriptions.</p>;

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6">
      {/* Top header */}
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl md:text-3xl font-bold text-[var(--primary-color)]">
          Subscription Management
        </h2>
      </div>

      {/* Active subscription banner - always on top */}
      {activeSub && (
        <div className="sticky top-4 z-40 mb-6 rounded-2xl border border-[var(--primary-color)] bg-[var(--alt-bg)] p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5 h-6 w-6 rounded-full bg-[var(--primary-color)] flex items-center justify-center text-white font-bold">
                ✓
              </div>
              <div>
                <div className="font-semibold text-[var(--text-color)]">
                  Subscription {activeSub?.status ? activeSub.status.toUpperCase() : 'ACTIVE'}
                </div>
                <div className="text-sm text-[var(--muted-color)]">
                  {activeTermName ? (
                    <>
                      Active on <span className="font-medium text-[var(--text-color)]">{activeTermName}</span>
                    </>
                  ) : (
                    'Active term detected.'
                  )}
                  {activeSubDetails?.currentPeriodEnd && (
                    <>
                      {' '}
                      • Renews on{' '}
                      <span className="font-medium text-[var(--text-color)]">
                        {fmtEpochSeconds(activeSubDetails.currentPeriodEnd)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {activeSubDetails?.planName && (
                <span className="px-3 py-1 rounded-full bg-[var(--card-bg)] border border-[var(--chart-grid-color)] text-sm">
                  Plan: <span className="font-medium">{activeSubDetails.planName}</span>
                </span>
              )}
              <button
                onClick={handleCancel}
                disabled={isProcessing}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {isProcessing ? 'Working...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inline alerts */}
      {bootError && (
        <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700">{bootError}</div>
      )}
      {inlineError && (
        <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-500 text-red-700">{inlineError}</div>
      )}
      {inlineSuccess && (
        <div className="mb-6 p-4 bg-green-100 border-l-4 border-green-500 text-green-700">
          {inlineSuccess}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Step 1: Plan */}
          <div className="bg-[var(--card-bg)] p-6 rounded-2xl shadow-lg transition-colors">
            <div className="flex items-center gap-2 mb-4">
              <span className="h-6 w-6 rounded-full bg-[var(--primary-color)] text-white text-sm flex items-center justify-center">
                1
              </span>
              <h3 className="text-xl font-semibold text-[var(--primary-color)]">Select Plan</h3>
            </div>
            {plans.length === 0 ? (
              <p className="text-[var(--muted-color)] text-sm">No plans available.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plans.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setPlanId(p.id)}
                    className={`text-left cursor-pointer p-4 rounded-xl border transition-all focus:outline-none focus:ring-2
                      ${
                        planId === p.id
                          ? 'border-[var(--primary-color)] bg-[var(--alt-bg)] ring-[var(--primary-color)]'
                          : 'border-[var(--chart-grid-color)] hover:border-[var(--primary-color)] hover:bg-[var(--card-hover-bg)]'
                      }`}
                  >
                    <div className="font-bold text-lg text-[var(--text-color)]">{p.name}</div>
                    <div className="text-[var(--primary-color)] text-2xl my-1">
                      {currencyFmt(p.price, p.currency)}
                      <span className="text-sm text-[var(--muted-color)]">/{p.durationUnit}</span>
                    </div>
                    {p.description && (
                      <div className="text-[var(--muted-color)] text-sm">{p.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Step 2: Term */}
          <div className="bg-[var(--card-bg)] p-6 rounded-2xl shadow-lg transition-colors">
            <div className="flex items-center gap-2 mb-4">
              <span className="h-6 w-6 rounded-full bg-[var(--primary-color)] text-white text-sm flex items-center justify-center">
                2
              </span>
              <h3 className="text-xl font-semibold text-[var(--primary-color)]">Select Term</h3>
            </div>

            {isActiveStatus(activeSub?.status) && activeTermName && (
              <p className="mb-3 text-sm text-[var(--muted-color)]">
                You already have an active subscription on{' '}
                <span className="font-medium text-[var(--text-color)]">{activeTermName}</span>. That
                term is hidden below to prevent duplicate purchases.
              </p>
            )}

            {payableTerms.length === 0 ? (
              <div className="p-4 rounded-lg border border-[var(--chart-grid-color)] bg-[var(--section-bg)] text-[var(--muted-color)]">
                No other payable terms are available right now.
              </div>
            ) : (
              <select
                value={termId}
                onChange={(e) => setTermId(e.target.value)}
                className="w-full p-3 border border-[var(--chart-grid-color)] rounded-lg bg-[var(--section-bg)] text-[var(--text-color)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                {payableTerms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{' '}
                    {t.startDate?.seconds && t.endDate?.seconds
                      ? `(${format(new Date(t.startDate.seconds * 1000), 'MMM d, yyyy')} - ${format(
                          new Date(t.endDate.seconds * 1000),
                          'MMM d, yyyy'
                        )})`
                      : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Step 3: Payment */}
          <div className="bg-[var(--card-bg)] p-6 rounded-2xl shadow-lg transition-colors">
            <div className="flex items-center gap-2 mb-4">
              <span className="h-6 w-6 rounded-full bg-[var(--primary-color)] text-white text-sm flex items-center justify-center">
                3
              </span>
              <h3 className="text-xl font-semibold text-[var(--primary-color)]">Payment Details</h3>
            </div>
            <CardElement
              onChange={handleCardChange}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: 'var(--text-color)',
                    '::placeholder': { color: 'var(--muted-color)' },
                  },
                },
                hidePostalCode: true,
              }}
              className="p-4 border border-[var(--chart-grid-color)] rounded-lg bg-[var(--section-bg)]"
            />
          </div>
        </div>

        {/* Right column: Order Summary */}
        <div className="space-y-6">
          <div className="bg-[var(--card-bg)] p-6 rounded-2xl shadow-lg transition-colors">
            <h3 className="text-xl font-semibold mb-4 text-[var(--primary-color)]">
              Order Summary
            </h3>
            <div className="space-y-3 text-[var(--text-color)]">
              <div className="flex justify-between">
                <span className="text-[var(--muted-color)]">Plan</span>
                <span>{selectedPlan.name || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--muted-color)]">Price</span>
                <span>{currencyFmt(selectedPlan.price, selectedPlan.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--muted-color)]">Term</span>
                <span>{selectedTerm.name || (payableTerms.length ? '—' : 'None available')}</span>
              </div>
              <div className="border-t border-[var(--chart-grid-color)] my-3"></div>
              <div className="flex justify-between font-bold">
                <span>Total</span>
                <span>{currencyFmt(selectedPlan.price, selectedPlan.currency)}</span>
              </div>
            </div>

            <button
              onClick={handleSubscribe}
              disabled={
                isProcessing || !planId || !termId || !cardComplete || payableTerms.length === 0
              }
              className={`w-full mt-6 py-3 rounded-lg font-medium text-white transition-all
                ${
                  isProcessing || !planId || !termId || !cardComplete || payableTerms.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-[var(--primary-color)] hover:bg-[var(--hover-color)]'
                }`}
            >
              {isProcessing ? 'Processing…' : 'Subscribe Now'}
            </button>

            {payableTerms.length === 0 && (
              <p className="mt-3 text-sm text-[var(--muted-color)]">
                There are no other terms available for purchase at the moment.
              </p>
            )}
          </div>

          {/* Active subscription details card (for convenience) */}
          {activeSubDetails && (
            <div className="bg-[var(--card-bg)] p-6 rounded-2xl shadow-lg transition-colors">
              <h3 className="text-xl font-semibold mb-4 text-[var(--primary-color)]">
                Your Active Subscription
              </h3>
              <div className="space-y-3 text-[var(--text-color)]">
                <div className="flex justify-between">
                  <span className="text-[var(--muted-color)]">Status</span>
                  <span className="font-medium">{activeSubDetails.status}</span>
                </div>
                {activeTermName && (
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-color)]">Term</span>
                    <span className="font-medium">{activeTermName}</span>
                  </div>
                )}
                {activeSubDetails.planName && (
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-color)]">Plan</span>
                    <span className="font-medium">{activeSubDetails.planName}</span>
                  </div>
                )}
                {activeSubDetails.currentPeriodStart && (
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-color)]">Period Start</span>
                    <span>{fmtEpochSeconds(activeSubDetails.currentPeriodStart)}</span>
                  </div>
                )}
                {activeSubDetails.currentPeriodEnd && (
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-color)]">Renews</span>
                    <span>{fmtEpochSeconds(activeSubDetails.currentPeriodEnd)}</span>
                  </div>
                )}
                <div className="border-t border-[var(--chart-grid-color)] my-3"></div>
                <button
                  onClick={handleCancel}
                  disabled={isProcessing}
                  className="w-full py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SubscriptionPage() {
  // Respect OS theme
  useEffect(() => {
    const root = document.documentElement;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = () => {
      if (mq.matches) root.classList.add('dark');
      else root.classList.remove('dark');
    };
    applyTheme();
    mq.addEventListener('change', applyTheme);
    return () => mq.removeEventListener('change', applyTheme);
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <SubscriptionPortal functionsBase={process.env.REACT_APP_FIREBASE_FUNCTIONS} />
    </Elements>
  );
}
